import { DecimalPipe, PercentPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Subscription, filter, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  dynamicFormsObservabilityTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  DynamicAnalyticsFields,
  DynamicAnalyticsFunnel,
  DynamicApiService,
  DynamicFormPublication,
  DynamicObservabilityMetrics
} from './dynamic-api.service';

type SelectOption = { label: string; value: string | null };

interface FieldMetricRow {
  fieldKey: string;
  label: string;
  errorCount: number;
  dropOffCount: number;
}

@Component({
  standalone: true,
  selector: 'ain-dynamic-analytics-dashboard',
  imports: [
    DecimalPipe,
    PercentPipe,
    FormsModule,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    AinDatePipe,
    RouterLink,
    ButtonModule,
    SelectModule,
    TableModule
  ],
  template: `
    <ain-page [title]="t('dynamic.analytics.title')" [description]="t('dynamic.analytics.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.analytics.infoTitle'"
        [descriptionKey]="'dynamic.analytics.infoDescription'"
        [bulletKeys]="[
          'dynamic.analytics.infoBulletFunnel',
          'dynamic.analytics.infoBulletFields',
          'dynamic.analytics.infoBulletLive'
        ]" />

      <div class="toolbar card">
        <label>
          <span>{{ t('dynamic.analytics.filter.publication') }}</span>
          <p-select
            [options]="publicationOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="publicationFilter"
            name="publicationFilter"
            [showClear]="true"
            [filter]="true"
            appendTo="body"
            (ngModelChange)="load()" />
        </label>
        <label>
          <span>{{ t('dynamic.analytics.filter.from') }}</span>
          <ain-date-input [(ngModel)]="fromDate" name="fromDate" (ngModelChange)="load()" />
        </label>
        <label>
          <span>{{ t('dynamic.analytics.filter.to') }}</span>
          <ain-date-input [(ngModel)]="toDate" name="toDate" (ngModelChange)="load()" />
        </label>
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        <span class="live-status" [class]="liveUpdateService.connectionState()">
          {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
        </span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else {
        <section class="section">
          <h3>{{ t('dynamic.analytics.funnel.title') }}</h3>
          @if (funnelError()) {
            <ain-state type="error" [title]="t('dynamic.analytics.funnel.error')" [message]="funnelError() ?? ''" />
          } @else if (funnel(); as data) {
            <div class="metrics">
              <article>
                <strong>{{ t('dynamic.analytics.funnel.starts') }}</strong>
                <span>{{ data.starts | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.funnel.completions') }}</strong>
                <span>{{ data.completions | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.funnel.dropoffs') }}</strong>
                <span>{{ data.dropoffs | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.funnel.completionRate') }}</strong>
                <span>{{ data.completionRate | percent:'1.0-1' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.funnel.avgTime') }}</strong>
                <span>{{ formatDuration(data.avgCompletionTimeMs) }}</span>
              </article>
            </div>

            <div class="funnel-bars" aria-hidden="true">
              <div class="funnel-bar funnel-bar--starts" [style.width.%]="funnelBarWidth(data.starts)"></div>
              <div class="funnel-bar funnel-bar--completions" [style.width.%]="funnelBarWidth(data.completions)"></div>
              <div class="funnel-bar funnel-bar--dropoffs" [style.width.%]="funnelBarWidth(data.dropoffs)"></div>
            </div>
          }
        </section>

        <section class="section">
          <h3>{{ t('dynamic.analytics.fields.title') }}</h3>
          @if (fieldsError()) {
            <ain-state type="error" [title]="t('dynamic.analytics.fields.error')" [message]="fieldsError() ?? ''" />
          } @else if (!fieldRows().length) {
            <ain-state type="empty" [title]="t('dynamic.analytics.fields.empty')" />
          } @else {
            <p-table [value]="fieldRows()" dataKey="fieldKey" [paginator]="true" [rows]="10">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('dynamic.analytics.fields.field') }}</th>
                  <th>{{ t('dynamic.analytics.fields.errors') }}</th>
                  <th>{{ t('dynamic.analytics.fields.dropOff') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.label }}</td>
                  <td>{{ row.errorCount | number:'1.0-0' }}</td>
                  <td>{{ row.dropOffCount | number:'1.0-0' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="section">
          <h3>{{ t('dynamic.observability.title') }}</h3>
          @if (observabilityError()) {
            <ain-state type="error" [title]="t('dynamic.observability.error')" [message]="observabilityError() ?? ''" />
          } @else if (observability(); as metrics) {
            <div class="metrics">
              <article>
                <strong>{{ t('dynamic.analytics.metrics.pipelineTotal') }}</strong>
                <span>{{ metrics.submissionPipelineTotal | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.metrics.pipelineSucceeded') }}</strong>
                <span>{{ metrics.submissionPipelineSucceeded | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.metrics.pipelineFailed') }}</strong>
                <span>{{ metrics.submissionPipelineFailed | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.metrics.validationErrorRate') }}</strong>
                <span>{{ metrics.submissionValidationErrorRate | percent:'1.0-1' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.metrics.migrationAvgDuration') }}</strong>
                <span>{{ formatDuration(metrics.migrationAvgDurationMs) }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.metrics.mutationTotal') }}</strong>
                <span>{{ metrics.mutationTotal | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.metrics.mutationSucceeded') }}</strong>
                <span>{{ metrics.mutationSucceeded | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.metrics.mutationFailed') }}</strong>
                <span>{{ metrics.mutationFailed | number:'1.0-0' }}</span>
              </article>
              <article>
                <strong>{{ t('dynamic.analytics.metrics.collectedAt') }}</strong>
                <span>{{ metrics.collectedAtUtc | ainDate }}</span>
              </article>
            </div>

            @if (metrics.recordsPerEntity.length) {
              <h4>{{ t('dynamic.analytics.metrics.recordsPerEntity') }}</h4>
              <p-table [value]="metrics.recordsPerEntity" dataKey="entityId">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('dynamic.analytics.metrics.entity') }}</th>
                    <th>{{ t('dynamic.analytics.metrics.recordCount') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>
                      <a [routerLink]="['/dynamic/entities', row.entityId]">{{ entityLabel(row.entityId) }}</a>
                    </td>
                    <td>{{ row.count | number:'1.0-0' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        </section>

        <section class="links">
          <a [routerLink]="['/dynamic/audit']">{{ t('nav.dynamicAudit') }}</a>
          <a [routerLink]="['/dynamic/forms']">{{ t('nav.dynamicForms') }}</a>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr)) auto auto;
      gap: 12px;
      align-items: end;
      margin-bottom: 16px;
    }

    .toolbar label {
      display: grid;
      gap: 6px;
      font-size: 0.875rem;
      color: #475569;
    }

    .live-status.connected { color: #15803d; }
    .live-status.connecting, .live-status.reconnecting { color: #a16207; }
    .live-status.disconnected { color: #b91c1c; }

    .section {
      margin-bottom: 20px;
    }

    .section h3,
    .section h4 {
      margin: 0 0 10px;
      color: #334155;
    }

    .metrics {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 10px;
      margin-bottom: 12px;
    }

    .metrics article {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fff;
      display: grid;
      gap: 6px;
    }

    .metrics strong {
      color: #6b7280;
      font-size: 0.85rem;
    }

    .metrics span {
      font-size: 1.2rem;
      font-weight: 700;
    }

    .funnel-bars {
      display: grid;
      gap: 8px;
      margin-top: 8px;
    }

    .funnel-bar {
      height: 10px;
      border-radius: 999px;
      min-width: 4%;
    }

    .funnel-bar--starts { background: #0ea5e9; }
    .funnel-bar--completions { background: #16a34a; }
    .funnel-bar--dropoffs { background: #f97316; }

    .links {
      display: flex;
      gap: 12px;
    }

    .links a {
      color: #1d4ed8;
      text-decoration: none;
      font-weight: 600;
    }

    .card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fff;
    }

    @media (max-width: 960px) {
      .toolbar { grid-template-columns: 1fr; }
      .metrics { grid-template-columns: 1fr; }
    }
  `]
})
export class DynamicAnalyticsDashboardComponent implements OnInit, OnDestroy {
  private readonly api = inject(DynamicApiService);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);

  private liveUnsubscribe: (() => Promise<void>) | null = null;
  private liveSubscription: Subscription | null = null;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly funnel = signal<DynamicAnalyticsFunnel | null>(null);
  readonly fieldMetrics = signal<DynamicAnalyticsFields | null>(null);
  readonly observability = signal<DynamicObservabilityMetrics | null>(null);
  readonly funnelError = signal('');
  readonly fieldsError = signal('');
  readonly observabilityError = signal('');
  readonly publications = signal<DynamicFormPublication[]>([]);
  readonly entities = signal<{ id: string; displayName: string }[]>([]);

  publicationFilter: string | null = null;
  fromDate = this.daysAgoIso(30);
  toDate = this.todayIso();

  liveStatusTranslationKey = liveStatusTranslationKey;

  readonly publicationOptions = computed<SelectOption[]>(() => [
    { label: this.t('common.all'), value: null },
    ...this.publications().map(item => ({
      label: item.slug,
      value: item.id
    }))
  ]);

  readonly fieldRows = computed<FieldMetricRow[]>(() => {
    const metrics = this.fieldMetrics();
    if (!metrics) {
      return [];
    }

    const keys = new Set<string>([
      ...Object.keys(metrics.errorDistribution ?? {}),
      ...Object.keys(metrics.dropOffByField ?? {})
    ]);

    return [...keys]
      .map(fieldKey => ({
        fieldKey,
        label: this.humanizeFieldKey(fieldKey),
        errorCount: metrics.errorDistribution?.[fieldKey] ?? 0,
        dropOffCount: metrics.dropOffByField?.[fieldKey] ?? 0
      }))
      .sort((left, right) => (right.errorCount + right.dropOffCount) - (left.errorCount + left.dropOffCount));
  });

  ngOnInit(): void {
    this.api.listPublications().subscribe({
      next: items => this.publications.set(items),
      error: () => this.publications.set([])
    });
    this.api.listEntities().subscribe({
      next: items => this.entities.set(items.map(item => ({ id: item.id, displayName: item.displayName || item.name }))),
      error: () => this.entities.set([])
    });
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    this.liveSubscription?.unsubscribe();
    void this.liveUnsubscribe?.();
  }

  load(showSpinner = true): void {
    if (showSpinner) {
      this.loading.set(true);
    }

    const from = this.toUtcBoundary(this.fromDate, false);
    const to = this.toUtcBoundary(this.toDate, true);

    forkJoin({
      funnel: this.api.getAnalyticsFunnel(this.publicationFilter, from, to).pipe(catchError(() => {
        this.funnelError.set(this.t('dynamic.analytics.funnel.error'));
        return of(null);
      })),
      fields: this.api.getFieldAnalytics(this.publicationFilter, from, to).pipe(catchError(() => {
        this.fieldsError.set(this.t('dynamic.analytics.fields.error'));
        return of(null);
      })),
      observability: this.api.getObservabilityMetrics().pipe(catchError(() => {
        this.observabilityError.set(this.t('dynamic.observability.error'));
        return of(null);
      }))
    }).subscribe({
      next: result => {
        if (result.funnel) {
          this.funnel.set(result.funnel);
          this.funnelError.set('');
        }
        if (result.fields) {
          this.fieldMetrics.set(result.fields);
          this.fieldsError.set('');
        }
        if (result.observability) {
          this.observability.set(result.observability);
          this.observabilityError.set('');
        }
        this.error.set('');
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  entityLabel(entityId: string): string {
    return this.entities().find(item => item.id === entityId)?.displayName ?? this.t('dynamic.analytics.metrics.entityUnknown');
  }

  formatDuration(value?: number | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '—';
    }
    if (value < 1000) {
      return `${Math.round(value)} ms`;
    }
    const seconds = Math.round(value / 1000);
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes}m ${remainder}s`;
  }

  funnelBarWidth(value: number): number {
    const funnel = this.funnel();
    if (!funnel) {
      return 0;
    }
    const max = Math.max(funnel.starts, funnel.completions, funnel.dropoffs, 1);
    return Math.max(4, (value / max) * 100);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const topic = dynamicFormsObservabilityTopic(tenantId);
    await this.liveUpdateService.subscribe(topic);
    this.liveSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.dynamicFormsObservabilityChanged))
      .subscribe(() => this.load(false));
    this.liveUnsubscribe = async () => {
      this.liveSubscription?.unsubscribe();
      await this.liveUpdateService.unsubscribe(topic);
    };
  }

  private humanizeFieldKey(fieldKey: string): string {
    return fieldKey
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  private daysAgoIso(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().slice(0, 10);
  }

  private todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private toUtcBoundary(dateValue: string, endOfDay: boolean): string {
    const suffix = endOfDay ? 'T23:59:59.999Z' : 'T00:00:00.000Z';
    return `${dateValue}${suffix}`;
  }
}
